import type { Rng } from '../../core/rng';
import type { OBB, Vec2 } from '../../geo/types';
import { obbCorners, pointInRing, ringBounds } from '../../geo/polygon';
import { obbPolygonOverlap } from '../../geo/sat';
import { buildableContains, createBuildableLookup } from './buildableLookup';
import type { BuildContext } from '../types';
import type { PolygonIndex } from '../../geo/polygonIndex';
import type { Lot, LotSource, ReservedFootprint } from './types';
import { generateLots } from './lots';
import { generateBacklots } from './backlots';
import { across } from './acrossPlot';
import { lotFrame } from './lotFrame';
import { claimLeftovers, type Taken } from './plotClaims';
import { claimBuildings, type ClaimPlot, type Span } from './claimBuildings';
import { inWoodcutYard } from './woodcutYards';
import { createPlotGround } from './widenAcross';
import { collectShoreSources, collectSquareSources, ijQuays } from './frontSources';
import { planPlots, type Plot } from './plots';
import { planGangen, type Gang } from './gangen';
import { createLotPlacer, type LotPlacer } from './lotPlacer';
import { runHousesToBanks } from './banks';
import { WOODCUT_GROUND, type WoodcutGround } from './woodcut/woodcutGround';
import {
  housesDrawn,
  offOpenGround,
  plotsToBuildThrough,
  plotsToFill,
} from './woodcut/woodcutPlots';
import { drawnBuildings } from './woodcut/woodcutInfill';
import { toFrontDepth } from './frontDepth';
import { lotKey } from './lotKey';
import { thinLastage, type ThinnedLastage } from './paintedLastage';
import { collectReservedLandmarks } from './landmarks/landmarkFeature';
import { LASTAGE_YARD, yardReservations } from './landmarks/lastageYard';
import { stocksReservations } from '../harbour/stocks';
import { shedLots } from './lastageSheds';
import { gardenHouseLots, gardenHouseReservations } from './lastageGardenHouses';
import { collectFortificationReserved } from './fortifications/fortificationFeature';
import { bridgeSpanReservations } from './bridgeSpans';
import { layoutWaterTest } from './fortifications/moat';
import { createCellGrid } from './cellGrid';
import { obbToAABB } from './lotObb';
import { planSurveyedBlocks } from './surveyed/surveyedPlan';
import { markWaterWalls } from './waterWalls';

export interface PlannedLots {
  readonly unresolvedReadings?: readonly string[];
  readonly houses: readonly Lot[];
  /**
   * The kamers along the gangen, the back houses and rear buildings on the plots, then the buildings
   * on the ground no plot reached and on the ground the woodcut draws built, numbered after the houses.
   */
  readonly backhouses: readonly Lot[];
  /** Each with the kamers built along it. */
  readonly gangen: readonly Gang[];
}

const STREET_KINDS: ReadonlySet<string> = new Set(['street', 'alley', 'quay', 'dike', 'road']);

function streetPriority(kind: string): number {
  switch (kind) {
    case 'quay':
      return 0;
    case 'street':
      return 1;
    case 'dike':
    case 'road':
      return 3;
    case 'alley':
      return 4;
    default:
      return 5;
  }
}

/*
 * PLAN Lots: a house fronts a street, quay, alley or square, never bare water; where no quay runs
 * along a bank, the plots of the street behind run to the water (plots.ts).
 */
function collectStreetSources(layout: BuildContext['layout']): readonly LotSource[] {
  return layout.streets
    .filter((street) => STREET_KINDS.has(street.kind))
    .map((street): LotSource => ({
      id: street.id,
      kind: street.kind as LotSource['kind'],
      centerline: street.centerline,
      widths: street.centerline.map(() => street.width),
      priority: streetPriority(street.kind),
    }));
}

/**
 * The ground lots keep clear of: the fortifications and the landmarks. A half-round tower turns its
 * round side to the water beside its wall, so it is reserved with the same water it is built with.
 */
export function lotReservations(layout: BuildContext['layout']): readonly ReservedFootprint[] {
  const { walls, towers, gates } = layout.fortifications;
  return [
    ...collectFortificationReserved(walls, towers, gates, layoutWaterTest(layout.water)),
    ...collectReservedLandmarks(layout.landmarks),
    ...yardReservations(LASTAGE_YARD),
    ...stocksReservations(),
    ...gardenHouseReservations(),
    ...bridgeSpanReservations(layout.bridges),
  ];
}

function frontLots(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildableIndex: PolygonIndex,
  reserved: readonly ReservedFootprint[],
  rng: Rng
): readonly Lot[] {
  const { lots } = generateLots({
    sources: [
      ...collectStreetSources(ctx.layout),
      ...collectShoreSources(
        ctx.site.waterEdges,
        ctx.layout.land.cityEnclosure,
        ijQuays(ctx.layout)
      ),
      ...collectSquareSources(ctx.layout.squares),
    ],
    buildableIndex,
    reserved,
    rng: rng.fork('lots'),
  });
  return lots;
}

/* The Lastage leaves gaps in the houses' numbers, so the buildings behind are numbered after the last. */
function nextIndex(lots: readonly Lot[]): number {
  return lots.reduce((next, lot) => Math.max(next, lot.index + 1), 0);
}

/**
 * The house lots along every source, the Lastage thinned by the painting and the woodcut
 * (`paintedLastage.ts`), with the stretch each woodcut-read Lastage side ran before the thinning.
 */
export function planFrontLots(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  rng: Rng,
  at: { readonly buildableIndex: PolygonIndex; readonly reserved: readonly ReservedFootprint[] } = {
    buildableIndex: createBuildableLookup(ctx.site.buildable, ctx.site.buildableIndex),
    reserved: lotReservations(ctx.layout),
  }
): ThinnedLastage {
  return thinLastage(frontLots(ctx, at.buildableIndex, at.reserved, rng), ctx.layout);
}

/*
 * A gang opens only where its stretch behind the house runs on buildable ground, clear of the other
 * houses and of every reservation; the stretch beside the house lies inside the house's own lot.
 */
/*
 * The gangen that can be opened: the passage has to fit, and the ground behind the courtyard has to
 * hold at least one kamer, the candidates' strips and the reservations standing in the placer, or the
 * gang would be cut into the block with nothing to line it: a convent's or a church's ground takes the
 * back of a plot whose house fronts the street before it (Sint Ursula's precinct behind the Rusland).
 */
function openGangen(
  candidates: readonly Gang[],
  houses: readonly Lot[],
  buildable: (p: Vec2) => boolean,
  reserved: readonly ReservedFootprint[]
): readonly Gang[] {
  const fronts = createCellGrid<Lot>(16);
  for (const house of houses) fronts.insert(house, obbToAABB(house.obb));
  const passage = createLotPlacer(buildable, houses, reserved);
  const lined = createLotPlacer(buildable, houses, [
    ...reserved,
    ...candidates.map((gang) => gang.strip),
  ]);
  return candidates.filter(
    (gang) =>
      // House placement tolerates small touching corners; a walking alley must remain entirely
      // clear. Only its own house may use the strip until it is narrowed to open the gang.
      !fronts.some(
        ringBounds(gang.strip.polygon.outer),
        (house) =>
          house.index !== gang.house.index && obbPolygonOverlap(house.obb, gang.strip.polygon.outer)
      ) &&
      passage.fits(gang.behind) &&
      gang.kamers.some((kamer) => lined.fits(kamer.obb))
  );
}

function placeKamers(
  gangen: readonly Gang[],
  placer: LotPlacer,
  startIndex: number
): readonly Lot[] {
  return gangen
    .flatMap((gang) => gang.kamers)
    .filter((kamer) => {
      if (!placer.fits(kamer.obb)) return false;
      placer.add(kamer);
      return true;
    })
    .map((kamer, i) => ({ ...kamer, index: startIndex + i }));
}

/* The house a building on a plot stands behind: its source less a rear or middle building's suffix, and its place. */
function houseKeyOf(lot: Lot): string {
  return lotKey({ ...lot, kind: 'house', sourceId: lot.sourceId.replace(/\/(rear|mid\d+)$/, '') });
}

/* How far behind the house's back wall a building starts and ends. */
function spanBehind(house: Lot, obb: OBB): Span {
  const { frontMid, facing, depth } = lotFrame(house);
  const behind = obbCorners(obb).map(
    (p) => (frontMid[0] - p[0]) * facing[0] + (frontMid[1] - p[1]) * facing[1] - depth
  );
  return { from: Math.min(...behind), to: Math.max(...behind) };
}

/** What stands on the plots once the back buildings are placed. */
interface Standing {
  readonly houses: readonly Lot[];
  readonly plots: readonly Plot[];
  readonly built: ReadonlySet<Plot>;
  readonly through: ReadonlySet<Plot>;
  readonly backlots: readonly Lot[];
  readonly gangen: readonly Gang[];
}

/* Each house's buildings: the back buildings behind it, and the kamers along its gang. */
function buildingsByHouse(s: Standing, house: ReadonlyMap<string, number>): Map<number, Lot[]> {
  const out = new Map<number, Lot[]>();
  const file = (key: string, lot: Lot) => {
    const i = house.get(key);
    if (i !== undefined) out.set(i, [...(out.get(i) ?? []), lot]);
  };
  s.backlots.forEach((lot) => {
    file(houseKeyOf(lot), lot);
  });
  s.gangen.forEach((gang) => {
    gang.kamers.forEach((kamer) => {
      file(lotKey(gang.house), kamer);
    });
  });
  return out;
}

/* The ground each house, its plot and its buildings take, and the gangen, which are no one's. */
function takenGround(
  s: Standing,
  house: ReadonlyMap<string, number>,
  buildings: ReadonlyMap<number, Lot[]>
): Taken[] {
  const plotRing = (plot: Plot) => obbCorners(across(plot.lot, 0, plot.depth, 1));
  return [
    ...s.houses.map((lot, owner) => ({ rings: [lot.corners], owner })),
    ...[...buildings].flatMap(([owner, lots]) =>
      lots.map((lot) => ({ rings: [lot.corners], owner }))
    ),
    ...s.plots
      .filter((plot) => plot.depth > 0)
      .map((plot) => ({ rings: [plotRing(plot)], owner: house.get(lotKey(plot.lot)) ?? -1 })),
    ...s.gangen.map((gang) => ({ rings: [gang.strip.polygon.outer], owner: -1 })),
  ];
}

/*
 * A walled house's plot as its claim is built on: built where the fill drew it, on a bank or along a
 * gang, or where the house itself ran on to the bank and left no plot.
 */
function claimPlots(
  s: Standing,
  buildings: ReadonlyMap<number, Lot[]>,
  walled: (p: Vec2) => boolean
): (ClaimPlot | null)[] {
  const plotOf = new Map(s.plots.map((plot) => [lotKey(plot.lot), plot]));
  const lined = new Set(s.gangen.map((gang) => lotKey(gang.house)));
  return s.houses.map((house, i) => {
    if (!walled(house.obb.center)) return null;
    const plot = plotOf.get(lotKey(house));
    const own = buildings.get(i) ?? [];
    return {
      house,
      built: !plot || s.built.has(plot) || lined.has(lotKey(house)),
      through: !!plot && s.through.has(plot),
      spans: own.map((lot) => spanBehind(house, lot.obb)),
    };
  });
}

/*
 * The ground no plot reaches, given to the houses around it and built on as the plot ground beside
 * it is (plotClaims.ts, claimBuildings.ts); the churches' and convents' ground and the gardens it
 * touches keep it open, and so do the yards the woodcut shows (woodcutYards.ts).
 */
function claimTheLeftovers(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  s: Standing,
  at: { readonly placer: LotPlacer; readonly reserved: readonly ReservedFootprint[] },
  startIndex: number
): readonly Lot[] {
  const house = new Map(s.houses.map((lot, i) => [lotKey(lot), i]));
  const buildings = buildingsByHouse(s, house);
  const enclosure = ctx.layout.land.cityEnclosure;
  const claims = claimLeftovers(
    {
      enclosure,
      buildable: ctx.site.buildable,
      reserved: at.reserved.map((r) => r.polygon),
      open: [
        ...collectReservedLandmarks(ctx.layout.landmarks).map((r) => r.polygon),
        ...ctx.layout.gardens.map((g) => ({ outer: g.polygon, holes: [] })),
      ],
    },
    takenGround(s, house, buildings)
  );
  const plots = claimPlots(s, buildings, (p) => pointInRing(p, enclosure));
  return claimBuildings(
    claims,
    plots,
    { placer: at.placer, inYard: inWoodcutYard, water: (p) => ctx.site.queries.isWater(p) },
    startIndex
  );
}

/**
 * Every house lot along the streets, quays, alleys, squares and the IJ shore, in the Lastage only as
 * many as the woodcut and the painting show (`paintedLastage.ts`) and its garden houses free of any
 * street (`lastageGardenHouses.ts`), the houses of the walled city as
 * deep as the sources give (`frontDepth.ts`), less those planned on ground the 1544 woodcut shows
 * open; then, on the plots behind them, the houses run on to the short plots at a bank, a warehouse
 * on every other bank plot, and on the plots the woodcut roofs (`woodcut/woodcutPlots.ts`) the gangen
 * with their kamers and the back buildings, none of them on ground the woodcut shows open; of these
 * plots and the bank plots, those it roofs from end to end are roofed through. Last, the ground the
 * woodcut draws built that none of these reached is built on (`woodcut/woodcutInfill.ts`).
 */
export function planLots(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  rng: Rng,
  ground: WoodcutGround = WOODCUT_GROUND
): PlannedLots {
  const civicReserved = lotReservations(ctx.layout);
  const enclosure = ctx.layout.land.cityEnclosure;
  const buildableIndex = createBuildableLookup(ctx.site.buildable, ctx.site.buildableIndex);
  const surveyed = planSurveyedBlocks(ctx, buildableIndex, civicReserved);
  const reserved = [...civicReserved, ...surveyed.reserved];
  const walled = (p: Vec2): boolean => pointInRing(p, enclosure);
  const lots = housesDrawn(
    planFrontLots(ctx, rng, { buildableIndex, reserved }).lots.map((lot) =>
      walled(lot.obb.center) ? toFrontDepth(lot, rng.fork('front')) : lot
    ),
    ground
  );
  const buildable = (p: Vec2): boolean => buildableIndex.contains(p);
  const banked = runHousesToBanks(
    lots,
    planPlots(lots, {
      buildable,
      reserved: buildableContains(reserved.map((r) => r.polygon)),
      walled,
      water: (p) => ctx.site.queries.isWater(p),
    }),
    createLotPlacer(buildable, lots, reserved, buildableIndex)
  );
  const filled = plotsToFill(banked.plots, ground);
  const built = banked.plots.filter((plot) => plot.bank || filled.has(plot));
  const through = plotsToBuildThrough(built, ground);
  const opened = openGangen(
    planGangen(
      banked.plots.filter((plot) => filled.has(plot)),
      rng.fork('gangen')
    ),
    banked.houses,
    buildable,
    reserved
  );
  const narrowed = new Map(opened.map((gang) => [lotKey(gang.house), gang.house]));
  const streetHouses = banked.houses.map((lot) => narrowed.get(lotKey(lot)) ?? lot);
  const gardens = nextIndex(streetHouses);
  const drafted = [...streetHouses, ...gardenHouseLots(gardens)];
  const placer = offOpenGround(
    createLotPlacer(
      buildable,
      drafted,
      [...reserved, ...opened.map((gang) => gang.strip)],
      buildableIndex
    ),
    ground
  );
  const first = nextIndex(drafted);
  const kamers = placeKamers(opened, placer, first);
  const standing = new Map(kamers.map((kamer) => [lotKey(kamer), kamer]));
  const gangen = opened
    .map((gang) => ({
      ...gang,
      kamers: gang.kamers.flatMap((k) => standing.get(lotKey(k)) ?? []),
    }))
    /* A gang the ground lined with no kamer is not cut into the block: its house keeps its front. */
    .filter((gang) => gang.kamers.length > 0);
  const lined = new Map(gangen.map((gang) => [lotKey(gang.house), gang.house]));
  const houses = [
    ...banked.houses.map((lot) => lined.get(lotKey(lot)) ?? lot),
    ...gardenHouseLots(gardens),
  ];
  const { backlots } = generateBacklots({
    plots: built.filter((plot) => !lined.has(lotKey(plot.lot))),
    through,
    placer,
    inYard: inWoodcutYard,
    ground: createPlotGround(banked.plots),
    rng: rng.fork('backlots'),
    startIndex: first + kamers.length,
  });
  const claimed = claimTheLeftovers(
    ctx,
    { houses, plots: banked.plots, built: new Set(built), through, backlots, gangen },
    { placer, reserved },
    first + kamers.length + backlots.length
  );
  const drawn = drawnBuildings(
    houses,
    ground,
    placer,
    first + kamers.length + backlots.length + claimed.length
  );
  const sheds = shedLots(first + kamers.length + backlots.length + claimed.length + drawn.length);
  const generic = [...houses, ...kamers, ...backlots, ...claimed, ...drawn, ...sheds];
  const start = nextIndex(generic);
  const authored = surveyed.lots.map((lot, i) => ({ ...lot, index: start + i }));
  return {
    unresolvedReadings: surveyed.unresolved,
    houses: [...houses, ...authored.filter((lot) => lot.kind === 'house')],
    backhouses: markWaterWalls(
      [
        ...kamers,
        ...backlots,
        ...claimed,
        ...drawn,
        ...sheds,
        ...authored.filter((lot) => lot.kind === 'backhouse'),
      ],
      (p) => ctx.site.queries.isWater(p)
    ),
    gangen,
  };
}
